// src/App.js
// CoryntiQ - Application principale avec routing

import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContextSupabase";
import { ThemeProvider } from "./context/ThemeContext";
import { HistoryProvider } from "./context/HistoryContext";
import LoginSupabase from "./components/LoginSupabase";
import Dashboard from "./components/Dashboard";
import SignupPage from "./pages/SignupPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import { Loader2 } from "lucide-react";

// Nettoyer l'ancien système d'auth au démarrage
const cleanupOldAuth = () => {
  const oldAuthKeys = ["user", "isAuthenticated", "entreprise", "token"];
  oldAuthKeys.forEach((key) => {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
    }
  });
};

// Composant de chargement
const LoadingScreen = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 flex items-center justify-center">
    <div className="text-center">
      <div className="relative w-20 h-20 mx-auto mb-4">
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-slate-400 to-slate-500"></div>
        <div
          className="absolute rounded-full bg-gradient-to-br from-sky-300 to-sky-400"
          style={{ top: "10%", left: "10%", width: "80%", height: "80%" }}
        ></div>
        <div
          className="absolute rounded-full bg-gradient-to-br from-slate-700 to-slate-800"
          style={{ top: "25%", left: "25%", width: "55%", height: "55%" }}
        ></div>
      </div>
      <Loader2 size={32} className="text-sky-400 animate-spin mx-auto mb-4" />
      <p className="text-slate-300">Chargement...</p>
    </div>
  </div>
);

// ============================================
// ROUTE PROTÉGÉE (authentification requise)
// ============================================
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// ============================================
// ROUTE PUBLIQUE (redirige si déjà connecté)
// ============================================
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// ============================================
// COMPOSANT DASHBOARD WRAPPER
// ============================================
const DashboardWrapper = () => {
  const { user, entreprise, signOut } = useAuth();

  return (
    <Dashboard
      user={{
        email: user?.email,
        name: user?.email?.split("@")[0] || "Utilisateur",
      }}
      entreprise={entreprise}
      onLogout={signOut}
    />
  );
};

// ============================================
// ROUTES DE L'APPLICATION
// ============================================
const AppRoutes = () => {
  useEffect(() => {
    cleanupOldAuth();
  }, []);

  return (
    <Routes>
      {/* Page d'accueil → redirige vers dashboard ou login */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Navigate to="/dashboard" replace />
          </ProtectedRoute>
        }
      />

      {/* Inscription - SANS PublicRoute car gère son propre flow multi-étapes */}
      <Route path="/signup" element={<SignupPage />} />

      {/* Pages légales - accessibles à tous */}
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />

      {/* Connexion (publique - redirige si déjà connecté) */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginSupabase />
          </PublicRoute>
        }
      />

      {/* Dashboard (protégée - requiert authentification) */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardWrapper />
          </ProtectedRoute>
        }
      />

      {/* Route catch-all → redirige vers accueil */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// ============================================
// APP PRINCIPALE AVEC PROVIDERS
// ============================================
const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <HistoryProvider>
            <AppRoutes />
          </HistoryProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
